import React, {useCallback, useRef, useState} from 'react';
import {Tooltip} from 'antd';
import type {TruncatedTextProps} from '@/types/components/truncatedText.types';

const DEFAULT_RATIO = 0.8;
const DEFAULT_MAX_WIDTH = 420;
const MIN_TOOLTIP_WIDTH = 160;

/** Обрезка длинного текста многоточием; полный текст — в Tooltip при наведении. */
const TruncatedText: React.FC<TruncatedTextProps> = ({
	text,
	strong,
	onClick,
	className,
	style,
	tooltipWidthRatio = DEFAULT_RATIO,
	tooltipMatchAnchor = true,
	tooltipMaxWidth: tooltipMaxWidthCap = DEFAULT_MAX_WIDTH,
}) => {
	const label = text?.trim() ? text : '—';
	const anchorRef = useRef<HTMLSpanElement>(null);
	const [tooltipWidth, setTooltipWidth] = useState<number>(tooltipMaxWidthCap);

	const refreshTooltipWidth = useCallback(() => {
		if (!tooltipMatchAnchor) {
			setTooltipWidth(tooltipMaxWidthCap);
			return;
		}
		const el = anchorRef.current;
		if (!el) return;
		const anchor =
			(el.closest('td, th') as HTMLElement | null) ?? el.parentElement ?? el;
		const measured = anchor.getBoundingClientRect().width;
		if (measured <= 0) {
			setTooltipWidth(tooltipMaxWidthCap);
			return;
		}
		const fromAnchor = Math.round(measured * tooltipWidthRatio);
		setTooltipWidth(
			Math.max(MIN_TOOLTIP_WIDTH, Math.min(fromAnchor, tooltipMaxWidthCap))
		);
	}, [tooltipMatchAnchor, tooltipWidthRatio, tooltipMaxWidthCap]);

	const mergedStyle: React.CSSProperties = {
		display: 'inline-block',
		verticalAlign: 'bottom',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: 0,
		maxWidth: '100%',
		width: '100%',
		fontWeight: strong ? 600 : undefined,
		cursor: onClick ? 'pointer' : undefined,
		color: onClick ? '#1677ff' : undefined,
		...style,
	};

	return (
		<Tooltip
			title={label}
			mouseEnterDelay={0.35}
			getPopupContainer={() => document.body}
			onOpenChange={open => {
				if (open) refreshTooltipWidth();
			}}
			styles={{
				container: {
					maxWidth: tooltipWidth,
					width: 'max-content',
					whiteSpace: 'normal',
					wordBreak: 'break-word',
					lineHeight: 1.45,
				},
			}}
		>
			<span
				ref={anchorRef}
				className={className}
				style={mergedStyle}
				onMouseEnter={refreshTooltipWidth}
				onClick={onClick}
				onKeyDown={
					onClick
						? e => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									onClick();
								}
							}
						: undefined
				}
				role={onClick ? 'button' : undefined}
				tabIndex={onClick ? 0 : undefined}
			>
				{label}
			</span>
		</Tooltip>
	);
};

export default TruncatedText;
